import { Injectable } from '@angular/core';
import { RecurringPaymentType, mapRecurringPaymentResponse } from '@finxone-platform/shared/sys-config-types';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, catchError, tap } from 'rxjs';
import {
  ClearRecurringPayments,
  GetRecurringPayments,
  PispCancelRecurringPayment,
  SetCurrentRecurringPayment,
} from '../actions/recurring-payments.action';
import { RecurringPaymentsService } from '../services/recurring-payments-service/recurring-payments.service';

export type RecurringPaymentsStateModel = {
  recurringPaymentList: RecurringPaymentType[];
  currentRecurringPayment?: RecurringPaymentType;
  isLoading: boolean;
};

const defaultRecurringPaymentsState: RecurringPaymentsStateModel = {
  recurringPaymentList: [],
  currentRecurringPayment: {} as RecurringPaymentType,
  isLoading: false,
};

@State<RecurringPaymentsStateModel>({
  name: 'recurringPayments',
  defaults: defaultRecurringPaymentsState,
})
@Injectable()
export class RecurringPaymentsState {
  constructor(private recurringPaymentsService: RecurringPaymentsService) {}

  @Selector()
  static getRecurringPayments(state: RecurringPaymentsStateModel) {
    return state;
  }

  @Selector()
  static getCurrentRecurringPayment(state: RecurringPaymentsStateModel) {
    return state.currentRecurringPayment;
  }

  @Selector()
  static getLoadingStatus(state: RecurringPaymentsStateModel) {
    return state.isLoading;
  }

  @Action(GetRecurringPayments)
  fetchRecurringPayments(ctx: StateContext<RecurringPaymentsStateModel>, action: GetRecurringPayments) {
    ctx.patchState({ isLoading: true });

    return this.recurringPaymentsService.getRecurringPayments(action.params).pipe(
      tap((response) => {
        ctx.patchState({
          recurringPaymentList: response.map(mapRecurringPaymentResponse),
          isLoading: false,
        });
      }),
      catchError<unknown, Observable<never>>((error) => {
        ctx.patchState({ isLoading: false });
        throw error;
      }),
    );
  }

  @Action(SetCurrentRecurringPayment)
  setCurrentRecurringPayment(
    ctx: StateContext<RecurringPaymentsStateModel>,
    action: SetCurrentRecurringPayment,
  ) {
    ctx.patchState({ currentRecurringPayment: action.recurringPayment });
  }

  @Action(ClearRecurringPayments)
  clearRecurringPayments(ctx: StateContext<RecurringPaymentsStateModel>) {
    ctx.setState({ ...defaultRecurringPaymentsState });
  }

  @Action(PispCancelRecurringPayment)
  pispCancelRecurringPayment(
    ctx: StateContext<RecurringPaymentsStateModel>,
    action: PispCancelRecurringPayment,
  ) {
    ctx.patchState({ isLoading: true });

    return this.recurringPaymentsService.pispCancelRecurringPayment(action.params).pipe(
      tap((response) => {
        const cancelledRecurringPayment = mapRecurringPaymentResponse(response);
        const recurringPayments = ctx.getState().recurringPaymentList;
        ctx.patchState({
          recurringPaymentList: recurringPayments.map((recurringPayment) =>
            recurringPayment.id === cancelledRecurringPayment.id
              ? cancelledRecurringPayment
              : recurringPayment,
          ),
          currentRecurringPayment: cancelledRecurringPayment,
          isLoading: false,
        });
      }),
      catchError<unknown, Observable<never>>((error) => {
        ctx.patchState({ isLoading: false });
        throw error;
      }),
    );
  }
}
